import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocketEvent } from './useSocket';
import { useNotificationStore } from '@/stores/notificationStore';
import { t } from '@/lib/i18n';
import { rorosKeys } from './roros/keys';

/**
 * ROROS WebSocket integration hook.
 * Subscribes to ROROS-specific events and invalidates React Query caches accordingly.
 * Call this once in the ROROS layout/guard component.
 *
 * B28: Also exposes optional callbacks for research events so callers can
 * manage local "running" state in addition to the global socket subscriptions.
 */
export interface RorosSocketCallbacks {
  onResearchStarted?: () => void;
  onResearchCompleted?: (data: { fieldsFound: string[] }) => void;
  onResearchFailed?: (data: { reason: string }) => void;
}

export function useRorosSocket(callbacks?: RorosSocketCallbacks): void {
  const queryClient = useQueryClient();
  const pushNotification = useNotificationStore((s) => s.push);

  // Work item status changed → invalidate ALL control-tower queries (work items,
  // verification inbox, wip status, overview, etc.) plus observability rollups.
  // v1.1.11 fix: pre-fix used camelCase keys ('workItems', 'controlTower',
  // 'inbox', 'wipStatus') that never matched the hyphenated keys defined in
  // rorosKeys, so the socket-driven auto-refresh was silently broken. Use the
  // canonical prefix from rorosKeys for prefix-match invalidation.
  //
  // v1.1.12: also push a cross-page notification when an item lands in QC,
  // so users who navigated away (e.g. to Control Tower) know a revision is
  // ready for verification. The toast is suppressed when the user is already
  // on the Verification Inbox (the page is auto-refreshing in that case).
  useSocketEvent<{ fromStatus?: string; toStatus?: string; workItemId?: string }>(
    'roros.work-item.status-changed',
    useCallback((data) => {
      queryClient.invalidateQueries({ queryKey: ['roros', 'control-tower'] });
      queryClient.invalidateQueries({ queryKey: ['roros', 'observability'] });

      if (data?.toStatus === 'quality_confirmation') {
        const onInbox = typeof window !== 'undefined'
          && window.location.pathname.startsWith('/roros/verification-inbox');
        if (!onInbox) {
          pushNotification({
            type: 'info',
            title: t('Verification Inbox'),
            message: t('A new item is ready for your quality check.'),
            duration: 8000,
          });
        }
      }
    }, [queryClient, pushNotification]),
  );

  // Work item created → same control-tower prefix invalidation.
  useSocketEvent('roros.work-item.created', useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['roros', 'control-tower'] });
  }, [queryClient]));

  // v1.1.19 — work item hard-deleted → invalidate the control-tower prefix
  // so the kanban board, verification inbox, badge, WIP indicators, and
  // work-item lists all refetch within ~1s of the delete completing.
  useSocketEvent('roros.work-item.deleted', useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['roros', 'control-tower'] });
    queryClient.invalidateQueries({ queryKey: ['roros', 'observability'] });
  }, [queryClient]));

  // QC report created → quality dashboards + verification inbox + observability.
  useSocketEvent('roros.qc.report-created', useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['roros', 'quality'] });
    queryClient.invalidateQueries({ queryKey: ['roros', 'observability'] });
    queryClient.invalidateQueries({ queryKey: rorosKeys.controlTower.verificationInbox });
  }, [queryClient]));

  // Metric threshold warning → show toast
  useSocketEvent<{ signal_key: string; signal_label: string; value: number; status: string }>(
    'roros.metric.threshold-warning',
    useCallback((data) => {
      pushNotification({
        type: 'warning',
        title: 'Threshold Warning',
        message: `${data.signal_label || data.signal_key}: value ${data.value} has entered warning zone`,
      });
      queryClient.invalidateQueries({ queryKey: ['roros', 'observability'] });
    }, [pushNotification, queryClient]),
  );

  // Metric threshold critical → show critical toast
  useSocketEvent<{ signal_key: string; signal_label: string; value: number; status: string }>(
    'roros.metric.threshold-critical',
    useCallback((data) => {
      pushNotification({
        type: 'error',
        title: 'Threshold Critical',
        message: `${data.signal_label || data.signal_key}: value ${data.value} is critical — immediate attention required`,
      });
      queryClient.invalidateQueries({ queryKey: ['roros', 'observability'] });
    }, [pushNotification, queryClient]),
  );

  // Cadence due → show cadence notification
  useSocketEvent<{ cadence_type: string; title: string }>(
    'roros.cadence.due',
    useCallback((data) => {
      pushNotification({
        type: 'info',
        title: 'Cadence Due',
        message: `${data.title || data.cadence_type} cadence review is due`,
      });
      queryClient.invalidateQueries({ queryKey: ['roros', 'cadence'] });
    }, [pushNotification, queryClient]),
  );

  // AI operator execution completed → refresh AI operator views.
  // v1.1.11 fix: hyphenated keys to match rorosKeys ('agents', 'task-delegations').
  useSocketEvent('roros.ai-operator.execution-completed', useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['roros', 'actors'] });
    queryClient.invalidateQueries({ queryKey: ['roros', 'agents'] });
    queryClient.invalidateQueries({ queryKey: ['roros', 'task-delegations'] });
  }, [queryClient]));

  // ─── Company Profile Research events (B28) ─────────────────────────────

  // research-started → notify local callback (no global cache change needed)
  useSocketEvent<{ orgId: string; websiteUrl: string }>(
    'roros.company-profile.research-started',
    useCallback((_data) => {
      callbacks?.onResearchStarted?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callbacks?.onResearchStarted]),
  );

  // research-completed → success toast + invalidate all company profile queries
  useSocketEvent<{ orgId: string; fieldsFound: string[] }>(
    'roros.company-profile.research-completed',
    useCallback((data) => {
      const count = data.fieldsFound?.length ?? 0;
      pushNotification({
        type: 'success',
        title: t('Research completed'),
        message: count > 0
          ? t('{{count}} suggestions applied to your company profile.').replace('{{count}}', String(count))
          : t('AI research completed. No new suggestions were found.'),
      });
      // Invalidate all overarching data so the page refetches
      queryClient.invalidateQueries({ queryKey: ['roros', 'overarching'] });
      callbacks?.onResearchCompleted?.(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pushNotification, queryClient, callbacks?.onResearchCompleted]),
  );

  // research-failed → error toast
  useSocketEvent<{ orgId: string; reason: string }>(
    'roros.company-profile.research-failed',
    useCallback((data) => {
      pushNotification({
        type: 'error',
        title: t('Research failed'),
        message: data.reason === 'timeout'
          ? t('AI research timed out. Please try again.')
          : data.reason === 'robots-disallowed'
          ? t('The website disallows automated crawling (robots.txt).')
          : t('AI research failed. Check the job log for details.'),
      });
      callbacks?.onResearchFailed?.(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pushNotification, callbacks?.onResearchFailed]),
  );
}
